/**
 * ProposalCard — the change the agent proposes, as a bordered card: what kind of change and at what
 * scope, the rule in plain language, what changes, and the actions. Nothing is saved until
 * Confirm change (BR-11). A proposal that clashes with an existing rule shows ConflictChoices instead.
 */

import { Link } from 'react-router-dom';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import type {
  ImprovementConflictView,
  ImprovementProposalView,
} from '../../../api/improvement-conversations.api';
import { agentName, proposalRuleText, scopeChipLabel, scopePhrase } from '../../../lib/playbook';
import { ConflictChoices, type ConflictChoice } from './ConflictChoices';

function changeKind(proposal: ImprovementProposalView): 'New rule' | 'Change' | 'Remove' {
  const ops = proposal.operations.map((o) => o.op);
  if (ops.length > 0 && ops.every((o) => o === 'remove_rule' || o === 'restore_default')) return 'Remove';
  if (ops.some((o) => o === 'add_rule')) return 'New rule';
  return 'Change';
}

export interface ProposalCardProps {
  proposal: ImprovementProposalView;
  conflict?: ImprovementConflictView;
  propertyName: string | undefined;
  /** The anchored case's property, for the scope switch; absent when the conversation has no property. */
  anchorProperty?: { id: string; name: string };
  busy: boolean;
  /** The turn budget is spent: the card is the last word, and the landlord can edit the rules directly. */
  budgetReached: boolean;
  onConfirm: () => void;
  onNotQuite: () => void;
  onSwitchScope: (message: string) => void;
  onChoose: (choice: ConflictChoice) => void;
}

export function ProposalCard({
  proposal,
  conflict,
  propertyName,
  anchorProperty,
  busy,
  budgetReached,
  onConfirm,
  onNotQuite,
  onSwitchScope,
  onChoose,
}: ProposalCardProps) {
  const noun = agentName(proposal.agentKind).toLowerCase();
  const scopeChip = scopeChipLabel(proposal.scope, propertyName);
  const switchTo =
    proposal.scope.kind === 'property'
      ? { label: 'All my properties', message: 'Apply this to all my properties instead.' }
      : anchorProperty
        ? { label: `Just ${anchorProperty.name}`, message: `Apply this just to ${anchorProperty.name} instead.` }
        : null;

  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-primary bg-card p-4"
      data-testid="improvement-proposal"
    >
      <div className="flex flex-wrap gap-2">
        <Badge variant="primary" data-testid="improvement-proposal-kind">
          {changeKind(proposal)}
        </Badge>
        <Badge variant="secondary" data-testid="improvement-proposal-scope">
          {scopeChip}
        </Badge>
      </div>
      <p className="m-0 text-[15px] font-semibold text-foreground" data-testid="improvement-proposal-rule">
        {proposalRuleText(proposal)}
      </p>
      <p className="m-0 text-[13px] text-muted-foreground">
        The {noun} follows this {scopePhrase(proposal.scope, propertyName)} from its next step.
        {proposal.scope.kind === 'property' ? " Your other properties don't change." : ''}
      </p>
      {conflict ? (
        <ConflictChoices proposal={proposal} conflict={conflict} disabled={busy} onChoose={onChoose} />
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button type="button" className="font-semibold" disabled={busy} onClick={onConfirm} data-testid="improvement-confirm">
            Confirm change
          </Button>
          {budgetReached ? (
            <Button asChild variant="outline">
              <Link to={`/settings/agents/${proposal.agentKind}/playbook`} data-testid="improvement-edit-rules">
                Edit my rules
              </Link>
            </Button>
          ) : (
            <>
              {switchTo && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => onSwitchScope(switchTo.message)}
                  data-testid="improvement-switch-scope"
                >
                  {switchTo.label}
                </Button>
              )}
              <Button type="button" variant="ghost" disabled={busy} onClick={onNotQuite} data-testid="improvement-not-quite">
                Not quite
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ProposalCard;
